import React, { useEffect, useState } from 'react';
import useFieldTracker from '../../../hooks/useFieldTracker';
import { phoneValidation } from '../../VgsInput/validations';
import BusinessTypeDropdowns from './BusinessTypeDropdowns';
import { OrgNode } from '../../../services/orgs/interfaces';
import VgsInput from '../../VgsInput';
import Address from '../../Address';
import useFieldRevealTracker from '../../../hooks/useFieldRevealTracker';
import {
  calculateEmptyFields,
  calculateFieldsToLoad,
  calculateInvalidFields,
} from './fields';
import VgsCollectValue from '../../VgsInput/VgsCollectValue';
import RequiredStatement from '../RequiredStatement';
import EditModal from '../../EditModal';
import Acknowledgement from './Acknowledgement';
import useModal from '../../../hooks/useModal';
import { FieldError } from '../../VgsInput/field';
import VgsCollectShim from '../../VgsInput/VgsCollectShim';

export default function Kyb({
  org,
  onValidChange,
  onBusinessTypeChange,
  onBusinessStructureChange,
  onAddressRegionChange,
  form,
  onRevealSuccess,
  onRevealFail,
  fieldErrors,
  token,
  raiseConfig,
  disabled,
}: {
  org?: OrgNode;
  onValidChange: (isValid: boolean) => void;
  onBusinessTypeChange: (type: string) => void;
  onBusinessStructureChange: (type: string) => void;
  onAddressRegionChange: (region: string) => void;
  form: VGSForm<{
    vaultCreateKybKycSubmit: VaultFormMutationResponse;
  }>;
  onRevealSuccess: () => void;
  onRevealFail: () => void;
  fieldErrors: FieldError[];
  token: string;
  raiseConfig?: {
    raiseCampaignId?: string;
    raiseUserId?: string;
  };
  disabled: boolean;
}): React.JSX.Element {
  const fieldsToLoad = calculateFieldsToLoad(org);
  const [emptyFields, onEmptyChange] = useFieldTracker(
    calculateEmptyFields(org),
  );
  const [invalidFields, onInvalidChange] = useFieldTracker(
    calculateInvalidFields(org),
  );
  const onFieldRevealSuccess = useFieldRevealTracker(
    onRevealSuccess,
    fieldsToLoad,
  );
  const { isOpen, toggleModal } = useModal();
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const [isValid, setIsValid] = useState(false);

  const sharedProps = {
    type: 'text' as VgsFieldType,
    required: true,
    showRequiredAsterisk: true,
    onEmptyChange,
    onInvalidChange,
    onRevealFail,
    form,
    fieldErrors,
    token,
  };

  useEffect(() => {
    const newValidity =
      emptyFields.length === 0 && invalidFields.length === 0 && isAcknowledged;
    if (newValidity !== isValid) {
      setIsValid(newValidity);
      onValidChange(newValidity);
    }
  }, [emptyFields, invalidFields, isAcknowledged, isValid, onValidChange]);

  return (
    <form>
      <VgsCollectShim form={form} />
      <EditModal
        isOpen={isOpen}
        onCancelClose={toggleModal}
        raiseConfig={raiseConfig}
      />
      <div className='md:flex mb-5'>
        <div className='md:w-full my-2'>
          <h1 className='page-title'>Organization Details</h1>
          <p className='page-text-primary'>
            Our payment partner requires us to collect and verify certain
            information about our customers. Should the information be
            unverifiable, we may require additional documentation. Protecting
            your information is our priority. Learn more about our{' '}
            <span>
              <a
                className='text-blue-600 '
                href='https://www.snapraise.com/legal-resources/'
                target='_blank'
                rel='noreferrer'
              >
                Terms of Service and Privacy Policy.
              </a>
            </span>
          </p>
          <RequiredStatement />
        </div>
      </div>
      <div className='md:flex'>
        <div className='md:w-full my-2'>
          <VgsInput
            name='kybPayableName'
            label='Payable Name'
            value={org?.fields.legal_name || org?.name}
            onRevealSuccess={onFieldRevealSuccess('kybPayableName')}
            {...sharedProps}
          />
        </div>
      </div>
      <div className='md:flex mb-5'>
        <div className='md:w-full'>
          <p className='page-text-secondary'>
            This information must be identical to IRS documentation and should
            be the information used to conduct business. This is the payable
            name that your check or direct deposit will be issued to.
          </p>
          {/* {fieldsToLoad.length > 0 && ( */}
          <a className='link' onClick={(): void => toggleModal()}>
            Report Incorrect Information
          </a>
          {/* )} */}
        </div>
      </div>
      <div className='md:flex mb-5 justify-between gap-2'>
        {!disabled && (
          <BusinessTypeDropdowns
            onTypeChange={onBusinessTypeChange}
            onStructureChange={onBusinessStructureChange}
          />
        )}

        <div className='md:w-1/3 my-2'>
          <VgsInput
            name='kybTaxId'
            label='Tax ID'
            placeholder='XX-XXXXXXX'
            maskInput={{
              mask: 'XX-XXXXXXX',
              maskChar: null,
              formatChar: { X: '[0-9]' },
            }}
            validations={[/^\d{2}-\d{7}$/]}
            value={org?.fields.ein_number}
            onRevealSuccess={onFieldRevealSuccess('kybTaxId')}
            {...sharedProps}
          />
        </div>
      </div>
      {/* if phone is falsy, collect value */}
      {!org?.fields.phone && !disabled && (
        <div className='md:flex mb-5'>
          <div className='md:w-full'>
            <VgsCollectValue
              name='kybPhoneNumber'
              label='Phone Number'
              placeholder='(XXX) XXX-XXXX'
              maskInput={{
                mask: '(999) 999-9999',
              }}
              validations={[phoneValidation]}
              {...sharedProps}
            />
          </div>
        </div>
      )}

      <Address
        entityName='kyb'
        onInvalidChange={sharedProps.onInvalidChange}
        form={form}
        value={
          org !== undefined
            ? {
                street: org.fields.street,
                city: org.fields.city,
                region: org.fields.state_code,
                postalCode: org.fields.zip_code,
              }
            : undefined
        }
        onRevealSuccess={onFieldRevealSuccess('kybAddress')}
        onRegionChange={onAddressRegionChange}
        token={token}
      />
      {!disabled && <Acknowledgement onUpdate={setIsAcknowledged} />}
    </form>
  );
}
